<template>
    <nav class="navbar navbar-expand-md navbar-primary navbar-light mx-auto pt-md-4 mb-md-4 px-md-2 col-lg-10 py-0"
         :class="{ 'navbar-drop': showAdminBar }">
        <div class="container-fluid main-wrapper d-flex justify-content-between">
            <a class="navbar-brand skin-navbar-brand" target="_blank" :href="parentCompanyLink"
               :style="parentCompanyLink === '' ? 'pointer-events: none;' : ''">
                <span class="visually-hidden">{{ site }} logo</span>
            </a>

            <button class="navbar-toggler border-0 fs-6" type="button" data-bs-toggle="collapse" data-bs-target="#navbarText"
                    aria-controls="navbarText" aria-expanded="false" aria-label="Toggle navigation">
                <span class="navbar-toggler-icon"></span>
            </button>

            <div v-if="showTitle" class="d-none d-xl-block">
                <h1>{{ title }}</h1>
            </div>

            <div class="collapse navbar-collapse d-md-flexjustify-content-md-end" id="navbarText">
                <ul class="navbar-nav mb-2 mb-lg-0" style="margin-right:6.5px;">
                    <li class="nav-item">
                        <a class="nav-link" :href="homeResource" :class="{ active: homeActive }">Home</a>
                    </li>

                    <li v-if="showStart" class="nav-item">
                        <a class="nav-link" :class="{ disabled: !startEnabled, active: startActive }" href="/home/userhome">Start</a>
                    </li>

                    <li v-if="showDesign" class="nav-item">
                        <a class="nav-link" :class="{ disabled: !designEnabled, active: designActive }"
                           href="/home/startdesign">Design</a>
                    </li>

                    <li v-if="showTrack" class="nav-item">
                        <a class="nav-link" :class="{ disabled: !trackEnabled, active: trackActive }" :href="trackResource">Track</a>
                    </li>

                    <li v-if="showPlan" class="nav-item">
                        <a class="nav-link" :class="{ disabled: !planEnabled, active: planActive }" href="/finish">Plan</a>
                    </li>

                    <li v-if="showOptions" class="nav-item">
                        <a class="nav-link" :class="{ disabled: !optionsEnabled, active: optionsActive }" href="/options">Options</a>
                    </li>

                    <li v-if="showLogin" class="nav-item">
                        <a class="nav-link" :class="{ disabled: false, active: false }" :href="loginResource">Log In</a>
                    </li>

                    <li v-if="showUserSettings" class="nav-item">
                        <a class="nav-link" @click="openUserSettingsModal" :class="{ active: userSettingsActive }"><i class="bi bi-gear"></i></a>
                    </li>

                    <li v-if="showGetSupport" class="nav-item">
                        <a class="nav-link" href="/support" :class="{ active: getSupportActive }">Get support</a>
                    </li>

                    <li v-if="showLogout" class="nav-item">
                        <a class="nav-link" href="/logoff" :class="{ active: logoutActive }"><i class="bi bi-box-arrow-right"></i></a>
                    </li>
                </ul>
            </div>
        </div>
    </nav>

    <modal title="User Settings" ref="userSettingsModal" idKey="userSettings" :isWide="true" :hideHeader="true">
        <user-settings-editor ref="userSettingsEditor" @exit="onUserSettingsExit" />
    </modal>
</template>

<script setup lang="ts">
    import { isAdminUser, normalizeResourcePath } from "@/guiide/ts/utilities";
    import View from "@/guiide/ts/view";
    import { ref, computed } from "vue";
    import { useStore } from "vuex";
    import Modal from "@/guiide/components/common/Modal.vue";
    import UserSettingsEditor from "@/guiide/components/userSettings/UserSettingsEditor.vue";

    const store = useStore();

    const userSettingsModal = ref<any>(null);

    const isInWorkflow = computed<boolean>(() => {
        const workflowViews = [
            View.UserHome,
            View.You,
            View.Requirements,
            View.Haves,
            View.Life,
            View.Design,
            View.Test,
            View.Finish,
            View.DBOptions,
            View.Support
        ];
        return workflowViews.includes(store.state.view);
    });

    const showAdminBar = computed<boolean>(() => isAdminUser());

    const parentCompanyLink = computed<string>(() => store.state.configuration.parentCompanyLink);

    const title = computed<string>(() => store.state.configuration.title);

    const showTitle = computed<boolean>(() => store.state.configuration.displayTitleInWorkflow);

    const showTrack = computed<boolean>(() =>
        isInWorkflow.value && store.state.configuration.nav.track.available
    );

    const trackEnabled = computed<boolean>(() => {
        if (store.state.retiree == null) return false;
        if (store.state.configuration.finishDisplaysNextButton) return true;
        return store.state.retiree.isRegistered;
    });

    const trackResource = computed<string>(() =>
        normalizeResourcePath(store.state.configuration.nav.track.resource)
    );

    const showPlan = computed<boolean>(() => !isInWorkflow.value);

    const planEnabled = computed<boolean>(() => !isInWorkflow.value);

    const showOptions = computed<boolean>(() => false); // store.state.configuration.nav.options.available

    const optionsEnabled = computed<boolean>(() => true);

    const homeResource = computed<string>(() =>
        normalizeResourcePath(store.state.configuration.nav.home.resource)
    );

    const showStart = computed<boolean>(() =>
        isInWorkflow.value && store.state.configuration.nav.start.available
    );

    const startEnabled = computed<boolean>(() => true);

    const showDesign = computed<boolean>(() =>
        isInWorkflow.value && store.state.configuration.nav.design.available
    );

    const designEnabled = computed<boolean>(() => true);

    const trackActive = computed<boolean>(() => false);
    const planActive = computed<boolean>(() => false);
    const homeActive = computed<boolean>(() => false);
    const startActive = computed<boolean>(() => false);

    const designActive = computed<boolean>(() => showStart.value);

    const optionsActive = computed<boolean>(() => false);

    const showLogin = computed<boolean>(() =>
        isInWorkflow.value && store.state.configuration.nav.login.available
    );

    const loginActive = computed<boolean>(() => true);

    const loginResource = computed<string>(() =>
        normalizeResourcePath(store.state.configuration.nav.login.resource)
    );

    const site = computed<string>(() => store.state.configuration.site);

    const showUserSettings = computed<boolean>(() => {
        if (store.state.retiree == null) {
            return false;
        }
        
        return (
            store.state.configuration.showUserSettings &&
            store.state.retiree.alreadyDrawingPension &&
            store.state.retiree.isRegistered
        );
    });

    const userSettingsActive = computed<boolean>(() => false);

    const showLogout = computed<boolean>(() => {
        if (store.state.retiree == null) {
            return false;
        }

        return (
            store.state.configuration.showLogout &&
            isInWorkflow.value &&
            !store.state.configuration.nav.login.available
        );
    });

    const logoutActive = computed<boolean>(() => false);

    const showGetSupport = computed<boolean>(() => {
        if (store.state.retiree == null) {
            return false;
        }

        return (
            store.state.configuration.showGetSupport &&
            isInWorkflow.value
        );
    });

    const getSupportActive = computed<boolean>(() => false);

    const openUserSettingsModal = () => {
        userSettingsModal.value?.showModal();
    };

    const onUserSettingsExit = () => {
        userSettingsModal.value?.closeModal();
    };
</script>

<style scoped>
    .navbar-collapse {
        flex-grow: 0;
    }

    @media (max-width: 767px) {
        .navbar-collapse {
            flex-grow: 1;
        }
    }

    nav {
        border-bottom: 1px;
    }

    @media (min-width: 1200px) {
        nav {
            height: 106px;
        }
    }

    @media (max-width: 768px) {
        .navbar-brand {
            margin-top: 0;
            margin-left: 3px !important;
        }
    }

    @media (max-width: 768px) {
        .navbar-primary {
            border-bottom: 1px solid #ccc;
        }
    }

    .pt-md-4 {
        padding-top: var(--navbar-padding-top) !important;
    }

    .navbar-brand {
        padding: 0;
        background-repeat: no-repeat;
    }

    .navbar-drop {
        margin-top: 82px;
    }

    .nav-link {
        border: none;
        margin: 12px;
        padding: 10px 18px;
        line-height: 1.5;
        font-size: 20px;
        font-weight: 600;
        text-align: center;
        white-space: nowrap;
        vertical-align: middle;
        border-bottom: 2px solid transparent;
    }

        .nav-link:hover,
        .nav-link.active {
            border-bottom: 2px solid var(--primary-color);
            cursor: pointer;
        }
</style>
